import React, { useRef, useState } from 'react';
import * as Yup from 'yup';
import { ScopeCertificateDates } from '../../../sc-models/scope-certificate-details-dto';
import FormBuilder from '../../../../../shared/components/form-builder/form-builder';
import moment from 'moment';
import { useParams } from 'react-router-dom';

interface ScopeCertificateDatesFormProps {
  formRef: any;
  datesData: ScopeCertificateDates;
  setDatesData: React.Dispatch<React.SetStateAction<ScopeCertificateDates>>;
}

const ScopeCertificateDatesForm: React.FC<ScopeCertificateDatesFormProps> = ({
  formRef,
  datesData,
  setDatesData,
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { formtype } = useParams();
  const hasSubmitSC: boolean = formtype === 'submited-sc';
  const toggleDrawer = () => {
    if (drawerOpen) resetForm();
    setDrawerOpen((prev) => !prev);
  };

  const resetForm = () => {
    setDatesData(datesData);
  };

  const formConfig = {
    pageName: "scope-certificate-details",
    initialValues: datesData,
    validationSchema: Yup.object().shape({
      scLastAuditStart: Yup.date()
        .nullable(),
      
      scLastAuditDate: Yup.date()
        .nullable()
        .test('audit-date-sequence', 'Last Audit Date must be after or equal to Last Audit Start Date', 
          function(value) {
            const startDate = this.parent.scLastAuditStart;
            if (!startDate || !value) return true; // Skip validation if either date is null
            return new Date(startDate) <= new Date(value);
          }),
      
      scDateOfIssue: Yup.date()
        .required('Date of Issue is required')
        .test('issue-date-sequence', 'Date of Issue must be after or equal to Last Audit Date',
          function(value) {
            const auditDate = this.parent.scLastAuditDate;
            if (!auditDate || !value) return true;
            return new Date(auditDate) <= new Date(value);
          }),
      
      scLastUpdated: Yup.date()
        .required('Last Updated date is required')
        .test('last-updated-sequence', 'Last Updated date must be after or equal to Date of Issue',
          function(value) {
            const issueDate = this.parent.scDateOfIssue;
            if (!issueDate || !value) return true;
            return new Date(issueDate) <= new Date(value);
          }),
      
      scValidUntil: Yup.date()
        .required('Valid Until date is required')
        .test('valid-until-sequence', 'Valid Until date must be after or equal to Last Updated date',
          function(value) {
            const lastUpdated = this.parent.scLastUpdated;
            if (!lastUpdated || !value) return true;
            return new Date(lastUpdated) <= new Date(value);
          }),
      
      scExtendedUntil: Yup.date()
        .nullable()
        .test('extended-until-sequence', 'Extended Until date must be after or equal to Valid Until date',
          function(value) {
            const validUntil = this.parent.scValidUntil;
            if (!validUntil || !value) return true;
            return new Date(validUntil) <= new Date(value);
          }),
      
      scPlaceOfIssue: Yup.string().required('Place of Issue is required'),
      scTransferAuditDate: Yup.date().nullable(),
    }),
    readOnly: hasSubmitSC,
    sections: [
      {
        title: 'Scope Certificate Dates',
        fields: [
          {
            label: 'SC Last Audit Start Date',
            name: 'scLastAuditStart',
            type: 'date',
            gridSize: { xs: 12, md: 6, lg: 3 },
            required: false,
            props: { placeholder: 'Select Last Audit Start date' },
          },
          {
            label: 'SC Last Audit Date',
            name: 'scLastAuditDate',
            type: 'date',
            gridSize: { xs: 12, md: 6, lg: 3 },
            required: false,
            props: { placeholder: 'Select Last Audit Date' },
          },
          {
            label: 'SC Date of Issue',
            name: 'scDateOfIssue',
            type: 'date',
            gridSize: { xs: 12, md: 6, lg: 3 },
            required: true,
            props: { placeholder: 'Select Date of Issue', maxDate: moment().add(100, "years") },
          },
          {
            label: 'SC Last Updated',
            name: 'scLastUpdated',
            type: 'date',
            gridSize: { xs: 12, md: 6, lg: 3 },
            required: true,
            props: { placeholder: 'Select Last Updated date', maxDate: moment().add(100, "years") },
          },
          {
            label: 'SC Valid Until',
            name: 'scValidUntil',
            type: 'date',
            gridSize: { xs: 12, md: 6, lg: 3 },
            required: true,
            props: {
              placeholder: 'Select Valid Until date',
              maxDate: moment().add(100, "years")
            },
          },
          {
            label: 'SC Extended Until',
            name: 'scExtendedUntil',
            type: 'date',
            gridSize: { xs: 12, md: 6, lg: 3 },
            required: false,
            props: { placeholder: 'Select Extended Until date',
              maxDate: moment().add(100, "years")
             },
          },
          {
            label: 'SC Place of Issue',
            name: 'scPlaceOfIssue',
            type: 'text',
            required: true,
            gridSize: { xs: 12, md: 6, lg: 3 },
            props: { placeholder: 'Enter Place of Issue' },
          },
          {
            label: 'SC Transfer Audit Date',
            name: 'scTransferAuditDate',
            type: 'date',
            gridSize: { xs: 12, md: 6, lg: 3 },
            required: false,
            props: { placeholder: 'Select Transfer Audit Date' },
          },
        ],
      },
    ],
    handleSubmit: (values: any) => {
      setDatesData(values);
      toggleDrawer();
    },
  };

  return <FormBuilder formConfig={formConfig} onSubmit={formConfig.handleSubmit} formRef={formRef} />;
};

export default ScopeCertificateDatesForm;

